import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Tabs, message, Space, Typography } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, HeartOutlined, StarOutlined } from '@ant-design/icons';
import { useNavigate} from 'react-router-dom';


const { Title, Text } = Typography;

// Mock用户数据，管理员有特殊标记
const MOCK_USERS = [
  { 
    id: 1, 
    username: 'admin', 
    password: '123456', 
    email: 'admin@example.com', 
    avatar: '👑',
    role: 'admin', // 管理员角色
    isAdmin: true // 管理员标志
  },
  { 
    id: 2, 
    username: 'user1', 
    password: '123456', 
    email: 'user1@example.com', 
    avatar: '😊',
    role: 'user',
    isAdmin: false
  },
  { 
    id: 3, 
    username: 'test', 
    password: '123456', 
    email: 'test@example.com', 
    avatar: '🌟',
    role: 'user',
    isAdmin: false
  },
];

// 创建动态背景元素
const BackgroundElements = () => {
  const [elements, setElements] = useState([]);

  useEffect(() => {
    // 创建心形
    const hearts = Array.from({ length: 15 }).map((_, i) => ({
      id: `heart-${i}`,
      type: 'heart',
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 5}s`,
      size: `${10 + Math.random() * 20}px`
    }));

    // 创建泡泡
    const bubbles = Array.from({ length: 10 }).map((_, i) => ({
      id: `bubble-${i}`,
      type: 'bubble',
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 10}s`,
      size: `${30 + Math.random() * 70}px`,
      duration: `${15 + Math.random() * 20}s`
    }));

    // 创建星星
    const stars = Array.from({ length: 8 }).map((_, i) => ({
      id: `star-${i}`,
      type: 'star',
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 2}s`,
      size: `${15 + Math.random() * 20}px`
    }));

    // 创建小动物装饰
    const animals = Array.from({ length: 6 }).map((_, i) => ({
      id: `animal-${i}`,
      type: 'animal',
      left: `${Math.random() * 100}%`,
      top: `${20 + Math.random() * 60}%`,
      emoji: ['🐰', '🐱', '🐶', '🐻', '🐼', '🦊'][i % 6],
      delay: `${Math.random() * 3}s`
    }));

    setElements([...hearts, ...bubbles, ...stars, ...animals]);
  }, []);

  return (
    <div className="auth-background">
      {elements.map(element => {
        if (element.type === 'heart') {
          return (
            <div
              key={element.id}
              className="heart"
              style={{
                left: element.left,
                animationDelay: element.delay,
                width: element.size,
                height: element.size
              }}
            />
          );
        }
        if (element.type === 'bubble') {
          return (
            <div
              key={element.id}
              className="bubble"
              style={{
                left: element.left,
                animationDelay: element.delay,
                width: element.size,
                height: element.size,
                animationDuration: element.duration
              }}
            />
          );
        }
        if (element.type === 'star') {
          return (
            <div
              key={element.id}
              className="star"
              style={{
                left: element.left,
                top: `${Math.random() * 100}%`,
                animationDelay: element.delay
              }}
            />
          );
        }
        if (element.type === 'animal') {
          return (
            <div
              key={element.id}
              className="cute-animal"
              style={{
                left: element.left,
                top: element.top,
                animationDelay: element.delay,
                fontSize: '24px'
              }}
            >
              {element.emoji}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

const Auth = () => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('login');
  const navigate = useNavigate();


  const onFinish = (values) => {
    if (activeTab === 'login') {
      // 登录逻辑
      const user = MOCK_USERS.find(
        u => u.username === values.username && u.password === values.password
      );
      
      if (user) {
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('currentUser', JSON.stringify({
          id: user.id,
          username: user.username,
          email: user.email,
          avatar: user.avatar,
          role: user.role,
          isAdmin: user.isAdmin
        }));
        message.success(`欢迎回来，${user.username}${user.isAdmin ? '（管理员）' : ''}！`);
        
        // 跳转到之前保存的页面或首页
        const redirectPath = localStorage.getItem('redirectPath') || '/';
        localStorage.removeItem('redirectPath');
        navigate(redirectPath);
      } else {
        message.error('用户名或密码错误！');
      }
    } else {
      // 注册逻辑
      const userExists = MOCK_USERS.some(u => u.username === values.username);
      if (userExists) {
        message.error('用户名已存在！');
        return;
      }
      
      if (values.password !== values.confirmPassword) {
        message.error('两次输入的密码不一致！');
        return;
      }
      
      // 创建新用户（在真实应用中应该发送到后端）
      const newUser = {
        id: MOCK_USERS.length + 1,
        username: values.username,
        password: values.password,
        email: values.email,
        avatar: '👤',
        role: 'user', // 新用户默认为普通用户
        isAdmin: false
      };
      
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('currentUser', JSON.stringify({
        id: newUser.id,
        username: newUser.username,
        email: newUser.email,
        avatar: newUser.avatar,
        role: newUser.role,
        isAdmin: newUser.isAdmin
      }));
      
      // 为新用户创建空的文章存储
      localStorage.setItem(`userPosts_${newUser.id}`, JSON.stringify([]));
      localStorage.setItem(`userLikes_${newUser.id}`, JSON.stringify({}));
      
      message.success('注册成功！已自动登录');
      navigate('/');
    }
  };

  const tabItems = [
    {
      key: 'login',
      label: (
        <span>
          <HeartOutlined style={{ marginRight: 8 }} />
          登录
        </span>
      ),
      children: (
        <Form form={form} onFinish={onFinish} layout="vertical" className="auth-form">
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名！' },
              { min: 3, message: '用户名至少3个字符！' },
              { max: 20, message: '用户名最多20个字符！' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="请输入用户名" 
              size="large"
              allowClear
              style={{ 
                height: '48px',
                fontSize: '16px'
              }}
            />
          </Form.Item>
          
          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码！' },
              { min: 6, message: '密码至少6个字符！' },
              { max: 20, message: '密码最多20个字符！' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="请输入密码" 
              size="large"
              style={{ 
                height: '48px',
                fontSize: '16px'
              }}
            />
          </Form.Item>
          
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              size="large" 
              block
              style={{ 
                marginTop: '24px',
                height: '48px',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              登录
            </Button>
          </Form.Item>
          
          <Space direction="vertical" style={{ width: '100%', textAlign: 'center', marginTop: '16px' }}>
            <Text type="secondary">
              <StarOutlined /> 管理员账号：admin / 123456
            </Text>
            <Text type="secondary">
              <StarOutlined /> 普通账号：user1 / 123456
            </Text>
            <Text type="secondary">
              <StarOutlined /> 普通账号：test / 123456
            </Text>
          </Space>
        </Form>
      ),
    },
    {
      key: 'register',
      label: (
        <span>
          <StarOutlined style={{ marginRight: 8 }} />
          注册
        </span>
      ),
      children: (
        <Form form={form} onFinish={onFinish} layout="vertical" className="auth-form">
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名！' },
              { min: 3, message: '用户名至少3个字符！' },
              { max: 20, message: '用户名最多20个字符！' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: '只能包含字母、数字和下划线！' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="请输入用户名（3-20位）" 
              size="large"
              allowClear
              style={{ 
                height: '48px',
                fontSize: '16px'
              }}
            />
          </Form.Item>
          
          <Form.Item
            name="email"
            rules={[
              { required: true, message: '请输入邮箱！' },
              { type: 'email', message: '请输入有效的邮箱地址！' }
            ]}
          >
            <Input 
              prefix={<MailOutlined />} 
              placeholder="请输入邮箱地址" 
              size="large"
              allowClear
              style={{ 
                height: '48px',
                fontSize: '16px'
              }}
            />
          </Form.Item>
          
          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码！' },
              { min: 6, message: '密码至少6个字符！' },
              { max: 20, message: '密码最多20个字符！' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="请输入密码（6-20位）" 
              size="large"
              style={{ 
                height: '48px',
                fontSize: '16px'
              }}
            />
          </Form.Item>
          
          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码！' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致！'));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="请再次输入密码" 
              size="large"
              style={{ 
                height: '48px',
                fontSize: '16px'
              }}
            />
          </Form.Item>
          
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              size="large" 
              block
              style={{ 
                marginTop: '24px',
                height: '48px',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              注册
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <div style={{ 
      position: 'relative', 
      minHeight: 'calc(100vh - 64px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      {/* 动态背景 */}
      <BackgroundElements />
      
      <div style={{ 
        maxWidth: 440, 
        width: '100%', 
        padding: '20px',
        zIndex: 1,
        position: 'relative'
      }}>
        <div className="auth-glass" style={{ padding: '40px 32px', borderRadius: '20px' }}>
          <Title 
            level={2} 
            style={{ 
              textAlign: 'center', 
              marginBottom: 40,
              background: 'linear-gradient(135deg, #ff9ebb, #ff9ed8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: '28px',
              fontWeight: '600'
            }}
          >
            {activeTab === 'login' ? '🌸 欢迎回来 🌸' : '✨ 加入我们 ✨'}
          </Title>
          
          <Card
            bordered={false}
            style={{ 
              background: 'transparent',
              boxShadow: 'none'
            }}
          >
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab} 
              centered
              items={tabItems}
              tabBarStyle={{ 
                marginBottom: 32,
                fontSize: '16px'
              }}
            />
          </Card>
          
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <Text type="secondary" style={{ fontSize: '15px' }}>
              {activeTab === 'login' 
                ? '还没有账号？' 
                : '已有账号？'
              }
              <Button 
                type="link" 
                onClick={() => setActiveTab(activeTab === 'login' ? 'register' : 'login')}
                style={{ 
                  color: '#ff9ebb',
                  fontWeight: '600',
                  fontSize: '15px',
                  padding: '0 4px'
                }}
              >
                {activeTab === 'login' ? '立即注册' : '立即登录'}
              </Button>
            </Text>
          </div>
        </div>
        
        <div style={{ 
          textAlign: 'center', 
          marginTop: 24,
          color: 'rgba(255, 255, 255, 0.8)',
          fontSize: '14px'
        }}>
          <Text>用 ❤️ 记录生活的每一刻</Text>
        </div>
      </div>
    </div>
  );
};

export default Auth;